import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [department, setDepartment] = useState('');
  const [position, setPosition] = useState('');
  const [phone, setPhone] = useState('');
  const [office, setOffice] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const extras = {};
      if (department.trim()) extras.department = department.trim();
      if (position.trim()) extras.position = position.trim();
      if (phone.trim()) extras.phone = phone.trim();
      if (office.trim()) extras.office = office.trim();
      await register(email, password, name, extras);
      setSuccess(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#F8FAF8] flex">
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
          <img 
            src="https://static.prod-images.emergentagent.com/jobs/f80a26d9-6372-4496-9e90-9bd77818ba19/images/f300ca52ae99289ad2b7cd90d476281037e2939c5419b407d9eda9a088d14c7b.png"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="w-full max-w-md"
          >
            <Card className="bg-white border-[#E2E8F0] shadow-lg">
              <CardHeader className="text-center space-y-4">
                <div className="flex justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_vitality-connect-3/artifacts/8w9yb7do_zdravestravovani_logo.png" 
                    alt="Zdravé stravování" 
                    className="h-16 object-contain"
                  />
                </div>
                <div className="w-16 h-16 mx-auto rounded-full bg-[#F0FDF4] flex items-center justify-center">
                  <Clock className="w-8 h-8 text-[#16A34A]" />
                </div>
                <CardTitle className="text-2xl font-bold text-[#052E16] font-['Manrope']" data-testid="register-success-title">
                  Registrace odeslána
                </CardTitle>
                <CardDescription className="text-[#57534E]">
                  Vaše registrace byla úspěšně odeslána. Vyčkejte na schválení administrátorem.
                </CardDescription>
              </CardHeader>
              <CardFooter className="flex justify-center">
                <Link to="/login">
                  <Button 
                    data-testid="back-to-login-btn"
                    className="bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold rounded-xl"
                  >
                    Zpět na přihlášení
                  </Button>
                </Link>
              </CardFooter>
            </Card>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAF8] flex">
      {/* Left side - decorative image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img 
          src="https://static.prod-images.emergentagent.com/jobs/f80a26d9-6372-4496-9e90-9bd77818ba19/images/f300ca52ae99289ad2b7cd90d476281037e2939c5419b407d9eda9a088d14c7b.png"
          alt="Background"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Right side - register form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <Card className="bg-white border-[#E2E8F0] shadow-lg">
            <CardHeader className="text-center space-y-4">
              <div className="flex justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_vitality-connect-3/artifacts/8w9yb7do_zdravestravovani_logo.png" 
                  alt="Zdravé stravování" 
                  className="h-16 object-contain"
                />
              </div>
              <CardTitle className="text-2xl font-bold text-[#052E16] font-['Manrope']">Registrace</CardTitle>
              <CardDescription className="text-[#57534E]">
                Vytvořte si účet v intranetu
              </CardDescription>
            </CardHeader>
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {error && (
                  <div className="p-3 bg-[#FEE2E2] border border-[#FECACA] rounded-xl text-[#991B1B] text-sm" data-testid="register-error">
                    {error}
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#1C1917]">Jméno</Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jan Novák"
                    required
                    data-testid="register-name-input"
                    className="bg-white border-[#E2E8F0] text-[#1C1917] placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:ring-[#16A34A]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#1C1917]">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vas@email.cz"
                    required
                    data-testid="register-email-input"
                    className="bg-white border-[#E2E8F0] text-[#1C1917] placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:ring-[#16A34A]"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#1C1917]">Heslo</Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    minLength={6}
                    data-testid="register-password-input"
                    className="bg-white border-[#E2E8F0] text-[#1C1917] placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:ring-[#16A34A]"
                  />
                </div>

                <div className="pt-2 border-t border-[#E2E8F0]">
                  <p className="text-xs text-[#57534E] mb-3 font-medium uppercase tracking-wider">Firemní údaje</p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label htmlFor="department" className="text-[#1C1917] text-sm">Oddělení</Label>
                      <Input
                        id="department"
                        type="text"
                        value={department}
                        onChange={(e) => setDepartment(e.target.value)}
                        placeholder="např. Marketing"
                        data-testid="register-department-input"
                        className="bg-white border-[#E2E8F0] text-[#1C1917] placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:ring-[#16A34A]"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label htmlFor="position" className="text-[#1C1917] text-sm">Pozice</Label>
                      <Input
                        id="position"
                        type="text"
                        value={position}
                        onChange={(e) => setPosition(e.target.value)}
                        placeholder="např. Manažer"
                        data-testid="register-position-input"
                        className="bg-white border-[#E2E8F0] text-[#1C1917] placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:ring-[#16A34A]"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label htmlFor="phone" className="text-[#1C1917] text-sm">Telefon</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+420 123 456 789"
                        data-testid="register-phone-input"
                        className="bg-white border-[#E2E8F0] text-[#1C1917] placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:ring-[#16A34A]"
                      />
                    </div>
                    
                    <div className="space-y-1">
                      <Label htmlFor="office" className="text-[#1C1917] text-sm">Kancelář</Label>
                      <Input
                        id="office"
                        type="text"
                        value={office}
                        onChange={(e) => setOffice(e.target.value)}
                        placeholder="Budova A, 2. patro"
                        data-testid="register-office-input"
                        className="bg-white border-[#E2E8F0] text-[#1C1917] placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:ring-[#16A34A]"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
              
              <CardFooter className="flex flex-col gap-4">
                <Button 
                  type="submit" 
                  disabled={isLoading}
                  data-testid="register-submit-btn"
                  className="w-full bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold rounded-xl transition-all shadow-sm"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Zaregistrovat se
                </Button>
                
                <p className="text-sm text-[#57534E] text-center">
                  Máte již účet?{' '}
                  <Link to="/login" className="text-[#16A34A] hover:text-[#15803D] font-medium transition-colors">
                    Přihlaste se
                  </Link>
                </p>
              </CardFooter>
            </form>
          </Card>
        </motion.div>
      </div>
    </div>
  );
}
